import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatError } from "@/api";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight, CheckCircle2, Users, BarChart3, Bell, Mail, Lock } from "lucide-react";

export default function LoginPage() {
    const [mode, setMode] = useState("login");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [showPass, setShowPass] = useState(false);
    const [remember, setRemember] = useState(true);
    const [loading, setLoading] = useState(false);
    const { login, register } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (mode === "login") {
                await login(email, password, remember);
            } else {
                if (!name.trim()) { toast.error("Introduceți numele"); setLoading(false); return; }
                await register(name, email, password);
            }
            navigate("/");
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setLoading(false);
        }
    };

    const features = [
        { icon: <CheckCircle2 size={15} />, text: "Task management vizual cu drag & drop" },
        { icon: <Users size={15} />, text: "Workspace-uri, echipe și board-uri structurate" },
        { icon: <BarChart3 size={15} />, text: "Dashboard managerial și rapoarte operaționale" },
        { icon: <Bell size={15} />, text: "Notificări in-app și alerte desktop" },
    ];

    return (
        <div className="min-h-screen flex bg-[#faf8ff] text-[#131b2e]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {/* Left Panel — brand & features (light, airy) */}
            <section className="hidden lg:flex w-1/2 bg-[#f2f3ff] relative overflow-hidden flex-col justify-between p-12">
                {/* Ambient decorative blobs */}
                <div className="absolute inset-0 z-0 pointer-events-none">
                    <div className="absolute -top-20 -left-16 w-96 h-96 rounded-full bg-[#002FA7]/10 blur-[90px]" />
                    <div className="absolute bottom-0 right-0 w-[28rem] h-[28rem] rounded-full bg-[#3654c8]/10 blur-[100px]" />
                    <div className="absolute top-1/3 right-1/4 w-72 h-72 rounded-full bg-[#dae2fd]/60 blur-[80px]" />
                </div>

                <div className="relative z-10 flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-[#002FA7] flex items-center justify-center text-white font-black text-xl shadow-lg shadow-[#002FA7]/20">T</div>
                    <div>
                        <h1 className="text-2xl font-black text-[#002FA7] tracking-tight leading-none">TOM</h1>
                        <p className="text-[11px] font-bold text-[#444653] uppercase tracking-[0.2em] mt-1">Team Operations Manager</p>
                    </div>
                </div>

                <div className="relative z-10 max-w-lg">
                    <h2 className="text-4xl font-bold text-[#131b2e] mb-4 tracking-tight leading-tight">Eficiență redefinită.</h2>
                    <p className="text-[#444653] text-lg leading-relaxed mb-10">
                        Gestionează-ți proiectele și echipele cu o claritate absolută. Centralizează taskuri, responsabilități și raportare într-o interfață rapidă, focusată pe livrare.
                    </p>
                    <div className="space-y-3">
                        {features.map((f, i) => (
                            <div key={i} className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-white/70 border border-white/60 flex items-center justify-center text-[#002FA7] shadow-sm">
                                    {f.icon}
                                </div>
                                <span className="text-sm text-[#444653] font-medium">{f.text}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="relative z-10 text-[#757685] text-xs tracking-wide">&copy; 2026 TOM — Team Operations Manager.</div>
            </section>

            {/* Right Panel — auth form */}
            <section className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 relative">
                {/* Ambient blur (mobile only) */}
                <div className="absolute inset-0 z-0 lg:hidden pointer-events-none">
                    <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#002FA7]/10 rounded-full blur-[80px]" />
                    <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-[#dae2fd]/50 rounded-full blur-[80px]" />
                </div>

                <div className="glass-panel w-full max-w-md rounded-2xl p-8 relative z-10">
                    {/* Mobile brand */}
                    <div className="lg:hidden mb-8 flex items-center justify-center gap-2">
                        <div className="w-10 h-10 rounded-xl bg-[#002FA7] flex items-center justify-center text-white font-black shadow">T</div>
                        <h1 className="text-2xl font-black text-[#002FA7] tracking-tight">TOM</h1>
                    </div>

                    <div className="mb-7">
                        <h2 className="text-2xl font-bold text-[#131b2e] mb-1.5 tracking-tight">
                            {mode === "login" ? "Bun venit înapoi" : "Creează cont nou"}
                        </h2>
                        <p className="text-sm text-[#444653]">
                            {mode === "login" ? "Conectează-te la platforma operațională." : "Înregistrează-te ca să accesezi platforma."}
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="auth-form">
                        {mode === "register" && (
                            <div>
                                <label className="block text-xs font-semibold text-[#444653] mb-1.5">Nume complet</label>
                                <input
                                    data-testid="register-name-input"
                                    type="text"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    placeholder="Ana Popescu"
                                    className="w-full px-4 py-3 bg-[#F1F5F9] border border-[#c4c5d6]/40 rounded-xl text-sm text-[#131b2e] placeholder-[#757685] focus:outline-none focus:border-[#002FA7] focus:border-2 transition-all"
                                    required
                                />
                            </div>
                        )}
                        <div>
                            <label className="block text-xs font-semibold text-[#444653] mb-1.5">Email</label>
                            <div className="relative">
                                <Mail size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#757685] pointer-events-none" />
                                <input
                                    data-testid="email-input"
                                    type="email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    placeholder="nume@companie.ro"
                                    className="w-full pl-11 pr-4 py-3 bg-[#F1F5F9] border border-[#c4c5d6]/40 rounded-xl text-sm text-[#131b2e] placeholder-[#757685] focus:outline-none focus:border-[#002FA7] focus:border-2 transition-all"
                                    required
                                    autoComplete="email"
                                />
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <label className="block text-xs font-semibold text-[#444653]">Parolă</label>
                                {mode === "login" && (
                                    <Link to="/forgot-password" className="text-xs font-semibold text-[#002FA7] hover:text-[#001e73] transition-colors">
                                        Ai uitat parola?
                                    </Link>
                                )}
                            </div>
                            <div className="relative">
                                <Lock size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#757685] pointer-events-none" />
                                <input
                                    data-testid="password-input"
                                    type={showPass ? "text" : "password"}
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className="w-full pl-11 pr-11 py-3 bg-[#F1F5F9] border border-[#c4c5d6]/40 rounded-xl text-sm text-[#131b2e] placeholder-[#757685] focus:outline-none focus:border-[#002FA7] focus:border-2 transition-all"
                                    required
                                    autoComplete={mode === "login" ? "current-password" : "new-password"}
                                />
                                <button type="button" onClick={() => setShowPass(!showPass)}
                                    data-testid="toggle-password-visibility"
                                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#757685] hover:text-[#002FA7] transition-colors">
                                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>

                        {mode === "login" && (
                            <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid="remember-me-label">
                                <input
                                    type="checkbox"
                                    data-testid="remember-me-checkbox"
                                    checked={remember}
                                    onChange={e => setRemember(e.target.checked)}
                                    className="h-4 w-4 rounded border-[#c4c5d6] text-[#002FA7] focus:ring-[#002FA7] accent-[#002FA7] cursor-pointer"
                                />
                                <span className="text-sm text-[#444653]">Ține-mă minte <span className="text-[#757685]">(30 zile)</span></span>
                            </label>
                        )}

                        <button
                            data-testid="auth-submit-btn"
                            type="submit"
                            disabled={loading}
                            className="group w-full flex items-center justify-center gap-2 bg-[#002FA7] hover:bg-[#001e73] text-white py-3.5 rounded-2xl text-sm font-semibold transition-colors disabled:opacity-50 shadow-sm shadow-[#002FA7]/20"
                        >
                            {loading ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <>
                                    <span>{mode === "login" ? "Autentificare" : "Înregistrare"}</span>
                                    <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
                                </>
                            )}
                        </button>
                    </form>

                    <div className="mt-8 text-center">
                        <p className="text-sm text-[#444653]">
                            {mode === "login" ? "Nu ai cont?" : "Ai deja cont?"}{" "}
                            <button
                                data-testid="auth-toggle-mode"
                                onClick={() => setMode(mode === "login" ? "register" : "login")}
                                className="font-semibold text-[#002FA7] hover:text-[#001e73] transition-colors"
                            >
                                {mode === "login" ? "Înregistrează-te" : "Conectează-te"}
                            </button>
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
}
